import React, { useEffect } from 'react';
import {
  ArrowRight,
  Bot,
  Building2,
  Check,
  ChevronRight,
  Code2,
  Globe,
  Headphones,
  Layers,
  MessageCircle,
  Monitor,
  Package,
  Rocket,
  Search,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Target,
  TrendingUp,
  Zap
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';
import { getWsjCalDemoBookingUrl } from '@/lib/wsjCalUrl';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';
import { WebDevHeroMockups } from '../components/web-dev/WebDevHeroMockups';
import { WebDevPlansSection } from '../components/web-dev/WebDevPlansSection';
import { WebDevRealProjectsSection } from '../components/web-dev/WebDevRealProjectsSection';

const whatIcons = [Globe, Rocket, ShoppingBag, Code2, Layers, Smartphone, Building2, Bot];
const diffIcons = [Sparkles, Zap, Search, Monitor, Shield, Zap, Layers, Monitor, Code2, Sparkles];
const processIcons = [MessageCircle, Monitor, Code2, Rocket];
const persuasionIcons = [Target, Zap, ShieldCheck];
const trustIcons = [Headphones, Code2, Smartphone, Package, Shield, Target];

const WebDevelopment = () => {
  const { t, language } = useLanguage();
  const p = t.webDevPage;
  const quoteUrl = getWsjCalDemoBookingUrl();

  useEffect(() => {
    const prev = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prev;
    };
  }, [p.metaTitle]);

  const openWhatsapp = () => {
    window.open(getWsjCommercialWhatsappUrl(p.whatsappMessage), '_blank', 'noopener,noreferrer');
  };

  const scrollToPortfolio = () => {
    document.getElementById('web-dev-portfolio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden">
      <Header />
      <main className="relative overflow-x-hidden pt-24 sm:pt-28 pb-8">
        <div className="absolute inset-0 tech-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute top-24 left-1/4 w-[min(420px,calc(100vw-2rem))] max-w-[100vw] h-[min(420px,70vw)] bg-sky-500/[0.07] rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-40 right-0 w-[min(380px,calc(100vw-2rem))] max-w-[100vw] h-[min(380px,65vw)] bg-indigo-500/[0.06] rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute top-1/2 left-0 w-[min(300px,calc(100vw-2rem))] h-[min(300px,50vw)] bg-amber-500/[0.04] rounded-full blur-[80px] pointer-events-none" />

        <div className="relative z-10 container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8">
          {/* HERO */}
          <section className="min-w-0 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 xl:gap-16 items-center mb-16 sm:mb-24 lg:mb-28">
            <div className="text-center lg:text-left pt-2 min-w-0">
              <div className="inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold tracking-wide text-sky-200/90 mb-5">
                <Code2 className="w-3.5 h-3.5 text-sky-400 shrink-0" aria-hidden />
                <span className="break-words text-center">{p.badge}</span>
              </div>
              <h1 className="text-[1.65rem] sm:text-4xl lg:text-[2.65rem] xl:text-5xl font-black text-white leading-[1.12] tracking-tight mb-5 break-words">
                {p.heroTitle}
              </h1>
              <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 mb-6 break-words">
                {p.heroSubtitle}
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-8">
                {p.heroBadges.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-white/65 backdrop-blur-sm"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3">
                <Button
                  asChild
                  size="lg"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 h-12 px-6"
                >
                  <a href={quoteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {p.btnQuote}
                    <ArrowRight className="w-4 h-4" aria-hidden />
                  </a>
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-emerald-500/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15 h-12 px-6"
                  onClick={openWhatsapp}
                >
                  <MessageCircle className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  {p.btnWhatsapp}
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-white/20 bg-white/[0.04] text-white hover:bg-white/10 h-12 px-6"
                  onClick={scrollToPortfolio}
                >
                  {p.btnProjects}
                  <ChevronRight className="w-4 h-4 ml-1" aria-hidden />
                </Button>
              </div>
            </div>
            <div className="mt-10 lg:mt-0 relative min-w-0">
              <WebDevHeroMockups mock={p.heroMock} />
            </div>
          </section>

          {/* PERSUASÃO — site bonito não basta */}
          <section id="web-dev-persuasion" className="max-w-5xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="rounded-[2rem] border border-sky-500/20 bg-gradient-to-br from-sky-500/[0.08] via-slate-950/70 to-indigo-950/40 backdrop-blur-xl p-6 sm:p-10 lg:p-12 overflow-hidden relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-sky-400/[0.08] rounded-full blur-[60px] pointer-events-none" aria-hidden />
              <div className="relative text-center mb-10">
                <h2 className="text-xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight break-words max-w-3xl mx-auto">
                  {p.sectionPersuasionTitle}
                </h2>
                <p className="text-white/50 mt-4 max-w-2xl mx-auto font-light leading-relaxed break-words">{p.sectionPersuasionLead}</p>
              </div>
              <div className="relative grid sm:grid-cols-3 gap-4 sm:gap-5">
                {p.persuasionPillars.map((pillar, i) => {
                  const Icon = persuasionIcons[i] || Sparkles;
                  return (
                    <div
                      key={pillar.title}
                      className="rounded-2xl border border-white/[0.08] bg-slate-950/50 backdrop-blur-md p-5 sm:p-6 text-center sm:text-left hover:border-sky-500/25 transition-colors duration-300"
                    >
                      <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/25 flex items-center justify-center mb-4 mx-auto sm:mx-0">
                        <Icon className="w-5 h-5 text-sky-400" aria-hidden />
                      </div>
                      <h3 className="text-sm font-bold text-white mb-2">{pillar.title}</h3>
                      <p className="text-xs sm:text-sm text-white/45 font-light leading-relaxed">{pillar.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* O QUE DESENVOLVEMOS */}
          <section id="web-dev-what" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">{p.sectionWhatTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionWhatLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-indigo-500 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {p.whatWeBuild.map((item, i) => {
                const Icon = whatIcons[i] || Sparkles;
                return (
                  <div
                    key={item.title}
                    className="group overflow-hidden rounded-2xl border border-white/[0.08] bg-slate-950/35 backdrop-blur-md p-5 sm:p-6 hover:border-sky-500/30 hover:bg-slate-950/55 hover:shadow-[0_20px_60px_-30px_rgba(56,189,248,0.25)] transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500/25 to-indigo-500/10 border border-sky-500/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <Icon className="w-5 h-5 text-sky-400" aria-hidden />
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 break-words">{item.title}</h3>
                    <p className="text-sm text-white/45 leading-relaxed font-light break-words">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* DIFERENCIAL */}
          <section id="web-dev-diff" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="rounded-[2rem] border border-white/[0.08] bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-indigo-950/30 backdrop-blur-xl p-6 sm:p-10 lg:p-14 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-72 h-72 bg-sky-500/[0.06] rounded-full blur-[80px] pointer-events-none" aria-hidden />
              <div className="relative">
                <div className="text-center mb-10 lg:mb-12">
                  <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">{p.sectionDiffTitle}</h2>
                  <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionDiffLead}</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                  {p.differentials.map((line, i) => {
                    const Icon = diffIcons[i] || Check;
                    return (
                      <div
                        key={line}
                        className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3.5 hover:border-sky-500/20 hover:bg-white/[0.05] transition-colors duration-200"
                      >
                        <Icon className="w-4 h-4 shrink-0 text-sky-400/90 mt-0.5" aria-hidden />
                        <span className="text-sm text-white/70 font-light leading-snug break-words">{line}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* PROCESSO */}
          <section id="web-dev-process" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionProcessTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionProcessLead}</p>
            </div>
            <div className="relative">
              <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-sky-500/30 to-transparent" aria-hidden />
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {p.processSteps.map((step, i) => {
                  const Icon = processIcons[i] || Sparkles;
                  return (
                    <div
                      key={step.title}
                      className="relative min-w-0 overflow-hidden rounded-2xl border border-white/[0.08] bg-slate-950/50 backdrop-blur-md p-5 pt-10 text-center lg:text-left hover:border-sky-500/25 transition-colors duration-300"
                    >
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 lg:left-5 lg:translate-x-0 w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white font-black text-sm flex items-center justify-center shadow-lg shadow-sky-900/30">
                        {i + 1}
                      </div>
                      <Icon className="w-6 h-6 text-sky-400/90 mx-auto lg:mx-0 mb-3 mt-2" aria-hidden />
                      <h3 className="text-sm font-bold text-white mb-2 leading-snug break-words">{step.title}</h3>
                      <p className="text-xs text-white/45 leading-relaxed font-light break-words">{step.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* TIPOS DE PROJETOS */}
          <section id="web-dev-types" className="max-w-5xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionTypesTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionTypesLead}</p>
            </div>
            {p.typesComparisonNote ? (
              <div className="mb-8 rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-4 sm:px-6 sm:py-5 max-w-4xl mx-auto">
                <p className="text-sm text-amber-100/75 font-light leading-relaxed text-center break-words">{p.typesComparisonNote}</p>
              </div>
            ) : null}
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <article className="rounded-3xl border border-white/[0.1] bg-slate-950/40 backdrop-blur-md p-6 sm:p-8 hover:border-sky-500/25 transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-sky-500/15 border border-sky-500/25 flex items-center justify-center mb-5">
                  <Globe className="w-6 h-6 text-sky-400" aria-hidden />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 break-words">{p.typeInstitutionalTitle}</h3>
                <p className="text-sm text-white/45 font-light leading-relaxed mb-5 break-words">{p.typeInstitutionalDesc}</p>
                <ul className="space-y-2.5 mb-6">
                  {p.typeInstitutionalItems.map((line) => (
                    <li key={line} className="flex gap-2.5 text-sm text-white/65 min-w-0">
                      <Check className="w-4 h-4 shrink-0 text-emerald-400/85 mt-0.5" aria-hidden />
                      <span className="break-words">{line}</span>
                    </li>
                  ))}
                </ul>
              </article>
              <article className="rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.06] via-slate-950/40 to-slate-950/60 backdrop-blur-md p-6 sm:p-8 hover:border-amber-500/35 transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center mb-5">
                  <Code2 className="w-6 h-6 text-amber-400" aria-hidden />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 break-words">{p.typeSystemsTitle}</h3>
                <p className="text-sm text-white/45 font-light leading-relaxed mb-5 break-words">{p.typeSystemsDesc}</p>
                <ul className="space-y-2.5 mb-6">
                  {p.typeSystemsItems.map((line) => (
                    <li key={line} className="flex gap-2.5 text-sm text-white/65 min-w-0">
                      <Check className="w-4 h-4 shrink-0 text-amber-400/85 mt-0.5" aria-hidden />
                      <span className="break-words">{line}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg font-black text-amber-300/95 tabular-nums">{p.typeSystemsPrice}</p>
              </article>
            </div>
          </section>

          <WebDevPlansSection language={language} copy={p} quoteUrl={quoteUrl} onPremiumContact={openWhatsapp} />

          {/* CONFIANÇA */}
          <section id="web-dev-trust" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-10 sm:mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">{p.sectionTrustTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionTrustLead}</p>
              <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto rounded-full mt-6" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {p.trustItems.map((item, i) => {
                const Icon = trustIcons[i] || Check;
                return (
                  <div
                    key={item.title}
                    className="group rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md p-5 sm:p-6 hover:border-amber-500/20 hover:bg-slate-950/55 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/5 border border-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <Icon className="w-5 h-5 text-amber-400" aria-hidden />
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 break-words">{item.title}</h3>
                    <p className="text-sm text-white/45 font-light leading-relaxed break-words">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* PORTFÓLIO */}
          <section id="web-dev-portfolio" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionPortfolioTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionPortfolioLead}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {p.portfolioItems.map((item) => (
                <article
                  key={item.title}
                  className="group overflow-hidden rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md hover:border-white/15 hover:shadow-[0_24px_80px_-40px_rgba(56,189,248,0.2)] transition-all duration-300"
                >
                  <div className={`relative h-44 sm:h-48 bg-gradient-to-br ${item.gradient} border-b border-white/[0.06] overflow-hidden`}>
                    {/* Browser chrome */}
                    <div className="absolute inset-x-3 top-3 rounded-t-xl border border-white/10 bg-slate-950/70 backdrop-blur-md overflow-hidden shadow-xl group-hover:scale-[1.02] transition-transform duration-500 origin-bottom">
                      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.06]">
                        <div className="w-2 h-2 rounded-full bg-red-400/70" />
                        <div className="w-2 h-2 rounded-full bg-amber-400/70" />
                        <div className="w-2 h-2 rounded-full bg-emerald-400/70" />
                        <div className="ml-auto flex-1 max-w-[40%] h-1.5 rounded-full bg-white/10" />
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="h-2 w-14 rounded-full bg-white/25" />
                          <div className="h-4 w-12 rounded-md bg-emerald-500/25 border border-emerald-500/30" />
                        </div>
                        <div className="h-10 rounded-lg bg-white/[0.06] border border-white/[0.05]" />
                        <div className="grid grid-cols-3 gap-1.5">
                          <div className="h-6 rounded bg-white/10" />
                          <div className="h-6 rounded bg-white/[0.06]" />
                          <div className="h-6 rounded bg-white/[0.06]" />
                        </div>
                      </div>
                    </div>
                    {item.result ? (
                      <div className="absolute bottom-3 right-3 rounded-lg border border-emerald-500/30 bg-emerald-950/60 backdrop-blur-md px-2.5 py-1 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-400" aria-hidden />
                        <span className="text-[10px] font-bold text-emerald-300 tabular-nums">{item.result}</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="p-5 sm:p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-400/80 mb-1">{item.category}</p>
                    <h3 className="text-base sm:text-lg font-bold text-white mb-2 break-words group-hover:text-sky-100 transition-colors">{item.title}</h3>
                    {item.desc ? (
                      <p className="text-sm text-white/45 font-light leading-relaxed mb-4 break-words">{item.desc}</p>
                    ) : null}
                    {item.tags?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {item.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-white/55"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <WebDevRealProjectsSection copy={p} />

          {/* TECNOLOGIAS */}
          <section id="web-dev-tech" className="max-w-5xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionTechTitle}</h2>
              <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light break-words">{p.sectionTechLead}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {p.technologies.map((tech) => (
                <span
                  key={tech}
                  className="inline-flex items-center rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white/75 hover:border-sky-500/30 hover:bg-sky-500/[0.08] hover:text-white transition-all duration-200"
                >
                  {tech}
                </span>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section id="web-dev-faq" className="max-w-3xl mx-auto mb-20 sm:mb-28 scroll-mt-28">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white">{p.sectionFaqTitle}</h2>
              <p className="text-white/50 mt-3 font-light break-words">{p.sectionFaqLead}</p>
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md px-4 sm:px-6 divide-y divide-white/[0.06]">
              {p.faqItems.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border-white/[0.06]">
                  <AccordionTrigger className="text-white hover:text-sky-300 hover:no-underline text-left font-semibold py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/50 font-light leading-relaxed pb-5">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* CTA FINAL */}
          <section
            id="web-dev-cta"
            className="max-w-5xl mx-auto mb-16 sm:mb-20 scroll-mt-28 relative rounded-[2rem] border border-white/[0.08] overflow-hidden shadow-[0_40px_120px_-48px_rgba(56,189,248,0.28)]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-sky-500/[0.14] via-slate-950/95 to-indigo-950/80 pointer-events-none" aria-hidden />
            <div className="absolute top-0 right-0 w-[min(100%,420px)] h-[280px] bg-indigo-500/[0.08] rounded-full blur-[100px] pointer-events-none" aria-hidden />
            <div className="absolute bottom-0 left-0 w-[min(100%,380px)] h-[240px] bg-amber-500/[0.08] rounded-full blur-[90px] pointer-events-none" aria-hidden />
            <div className="relative px-6 py-10 sm:px-12 sm:py-14 lg:px-16 lg:py-16 text-center">
              <h2 className="text-2xl sm:text-4xl lg:text-[2.35rem] font-black text-white tracking-tight mb-4 max-w-3xl mx-auto leading-tight break-words px-1">
                {p.finalCtaTitle}
              </h2>
              <p className="text-white/55 font-light mb-10 max-w-2xl mx-auto leading-relaxed text-base sm:text-lg break-words px-1">
                {p.finalCtaSubtitle}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                <Button
                  asChild
                  size="lg"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold px-8 h-12 shadow-lg shadow-amber-900/25"
                >
                  <a href={quoteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                    {p.btnQuote}
                    <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                  </a>
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-emerald-500/40 bg-emerald-500/10 text-emerald-50 hover:bg-emerald-500/18 h-12 px-8 font-semibold"
                  onClick={openWhatsapp}
                >
                  <MessageCircle className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  {p.btnWhatsapp}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WebDevelopment;
